import React, { Fragment, useState } from 'react'

import { Listbox, Transition } from '@headlessui/react'

import { Icon } from 'components/Icon'

export interface SelectProps {
  /**
   * Show a placeholder.
   */
  placeholder?: string
  /**
   * If `true`, the button will be disabled.
   */
  isDisabled?: boolean
  /**
   * If `true`, the select will be required.
   */
  isRequired?: boolean
  /**
   * If `true`, the error message will be shown.
   */
  error?: boolean
  /**
   * If `true`, the input will take up the full width of its container.
   */
  isFullWidth?: boolean
  /**
   * Select options
   */
  options?: SelectOptions[]
  /**
   * Selected option id
   */
  defaultValue?: string
  /**
   * Return selected value as string
   */
  onUserSelect?: (value: string) => void
  /**
   * The html input name
   */
  selectName: string
  /**
   * The html input id
   */
  id: string
}

export const Select = React.forwardRef<HTMLDivElement, SelectProps>(
  (props, ref) => {
    const {
      placeholder = 'Select option',
      isDisabled,
      error,
      isFullWidth,
      options,
      defaultValue,
      onUserSelect,
      isRequired,
      id,
      selectName,
    } = props
    const width = isFullWidth ? 'w-full' : 'w-auto'

    const [selected, setSelected] = useState(defaultValue)

    const handleSelectValue = (value: string): void => {
      setSelected(value)
      onUserSelect(value)
    }

    return (
      <Listbox
        value={selected}
        onChange={handleSelectValue}
        disabled={isDisabled}
      >
        {({ open }) => (
          <div className="relative mt-1" ref={ref}>
            <Listbox.Button
              id={id}
              name={selectName}
              aria-required={isRequired}
              className={`relative ${width} bg-gray-50 border border-gray rounded-lg pl-3 pr-10 py-2 cursor-default box-border sm:text-sm focus:bg-error-100 focus:border-flamingo-500
              ${
                error
                  ? 'text-flamingo-500 border-flamingo-500 bg-error-50'
                  : 'text-full border-gray bg-gray-50'
              }  
              ${isDisabled && 'text-dark-700'}`}
            >
              <span className="flex items-center">
                <span className="block truncate">
                  {' '}
                  {selected && options.find((option) => option.id === selected)
                    ? options.find((option) => option.id === selected).value
                    : placeholder}
                </span>
              </span>
              <span className="absolute inset-y-0 right-0 flex items-center pr-2 ml-3 pointer-events-none">
                <Icon
                  icon="chevron-down-2"
                  aria-hidden="true"
                  iconSize="15px"
                  className="w-5 h-5"
                />
              </span>
            </Listbox.Button>

            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options
                static
                className="absolute z-10 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-lg shadow-lg max-h-56 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
              >
                {options?.map((option) => (
                  <Listbox.Option
                    key={option.id}
                    className={({ active }) => `${
                      active ? 'bg-flamingo-100' : 'bg-light-50'
                    } 
                         ${option.disabled ? 'text-dark-700' : 'text-full'}
                        'cursor-default select-none relative py-2 pl-3 pr-9'`}
                    value={option.id}
                    disabled={option.disabled}
                  >
                    {() => (
                      <div className="flex items-center">
                        <span
                          className={`${
                            selected ? 'font-semibold' : 'font-normal'
                          }'ml-3 block truncate'`}
                        >
                          {option.value}
                        </span>
                      </div>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        )}
      </Listbox>
    )
  }
)
